/**
 * What a source is called, everywhere a person reads one.
 *
 * A real provider has a brand: two Xaria accounts share the "Xaria" name
 * (and the "Xaria" chart series) whatever they were labelled. A MECHANISM -
 * a Google sheet, an upload, an inbox - has no brand of its own, so the
 * account's own label is the name: "Mobilynx G Sheet", not "Custom source".
 * The providerLabel (a recognised provider's display name captured at
 * add-time) still outranks the built-in table for real providers.
 */
export const PROVIDER_NAMES: Record<string, string> = {
  swaarm: 'Xaria',
  trafficguru: 'Oxfeeds',
  aemdays: 'AEMDays',
  justforspends: 'JustForSpends',
  clarityseek: 'ClaritySeek',
  generic: 'Custom source',
  upload: 'Uploads',
  email: 'Email',
};

const MECHANISMS = new Set(['generic', 'upload', 'email']);

export function sourceName(a: {
  provider: string;
  providerLabel?: string | null | undefined;
  label?: string | null | undefined;
}): string {
  const branded = a.providerLabel || PROVIDER_NAMES[a.provider] || a.provider;
  return MECHANISMS.has(a.provider) && a.label ? a.label : branded;
}

/** The provider's name alone - chart series, breakdowns by provider - where
 * no single account is in view. */
export function providerName(provider: string, providerLabel?: string | null): string {
  return providerLabel || PROVIDER_NAMES[provider] || provider;
}

/** A dropdown entry: the rep, then the source's name. */
export function accountOption(a: {
  rep: string;
  provider: string;
  providerLabel?: string | null | undefined;
  label?: string | null | undefined;
}): string {
  return `${a.rep} — ${sourceName(a)}`;
}

/**
 * The source axis of the reports (the "by provider" breakdown, the chart
 * series, the "All sources" filter): a real provider is keyed by its slug and
 * named by its brand; a mechanism account - a pasted sheet or URL, an upload,
 * an inbox - is keyed `acct:<id>` and named by its own label, so four pasted
 * sheets are four lines and never one "Custom source" (2026-09-15).
 */
export const ACCOUNT_KEY = 'acct:';

export function sourceKeyName(
  key: string,
  label?: string | null,
  accounts?: ReadonlyMap<
    string,
    {
      provider: string;
      providerLabel?: string | null | undefined;
      label?: string | null | undefined;
    }
  >,
): string {
  if (key.startsWith(ACCOUNT_KEY)) {
    const id = key.slice(ACCOUNT_KEY.length);
    if (label) return label;
    const a = accounts?.get(id);
    return a ? sourceName(a) : id;
  }
  return providerName(key, label);
}
